<template>
    <div class="body-wrapper bg-[#F5F6FA] rounded-xl">
        <!-- Hidden TraditionalStyleThemeMain for PDF generation (always rendered, off-screen, real size) -->
        <div style="position: absolute; top: -9999px; left: -9999px; width: 800px; min-height: 1120px;" ref="caseForSupportRef">
            <TraditionalStyleThemeMain />
        </div>
        <div class="flex">
            <!-- Left Sidebar -->
            <div class="left-col-bar border-e border-[#E0E0E0] border-solid min-w-[420px] w-[420px] p-4 h-[calc(100vh-108px)]">
                <div class="flex flex-col h-full gap-6 bg-white rounded-[12px] overflow-y-auto">
                    <!-- Welcome and Instructions -->
                    <div class="m-6">
                        <h1 class="font-minion font-semibold text-[36px] leading-[105%] tracking-[-0%] text-[#414141]">
                            Hello {{ authStore.user?.firstName || 'User' }} –<br />Complete Your Plan, Generate Your Tools
                        </h1>
                        <p class="font-sans font-normal text-[14px] leading-[141%] tracking-[-0%] text-[#414141] mt-2 mb-4">
                            Ask a Fundraiser© guides you through building a strong Fundraising Plan—helping you define goals, strategies, and key details. As you complete each section, the system gathers your inputs and prepares Fundraising Collateral—customized resources to support your fundraising efforts and operations.
                        </p>
                        <div class="mb-2 font-sans">
                            <div class="font-sans font-semibold text-[14px] leading-[141%] tracking-[-0%] text-[#00423D] mb-1">
                                <h4>How It Works:</h4>
                            </div>
                            <ol class="list-decimal list-inside text-[14px] text-[#414141] font-['Open_Sans'] font-normal space-y-1">
                                <li><span class="">Complete Your Fundraising Plan</span> – Ask a Fundraiser© gathers your inputs as you go.</li>
                                <li><span class="">Unlock Fundraising Collateral</span> – Your plan generates tailored templates, reports, and resources.</li>
                                <li><span class="">Put It to Work</span> – Use your collateral to fundraise, engage supporters, and grow your organization.</li>
                            </ol>
                            <div class="mt-4 text-[#414141] text-[13px] font-sans font-normal italic leading-[141%] tracking-[-0%]">
                                <span class="inline-block align-middle">💡</span> Your Fundraising Plan powers your resources—<span>complete it to unlock what you need.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Right Content: Card Grid -->
            <div class="right-col-content w-full p-4 h-[calc(100vh-108px)] overflow-y-auto">
                <div class="text-[#414141] font-sans">
                    <!-- Tabs -->
                    <div class="mb-6">
                        <div class="flex items-center space-x-4 border-b border-gray-200">
                            <h5 class="text-[1rem] leading-[1.75rem] font-semibold text-[#414141]">Show Resources:</h5>
                            <button
                                v-for="tab in tabs"
                                :key="tab"
                                @click="selectedTab = tab"
                                :class="[
                                    'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
                                    selectedTab === tab
                                        ? 'border-[#00423D] text-[#00423D]'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                ]"
                            >
                                {{ tab }}
                            </button>
                        </div>
                    </div>

                    <!-- Loading State -->
                    <div v-if="isLoading" class="text-gray-600 font-sans py-4">
                        <p>Loading fundraising collateral status...</p>
                    </div>

                    <!-- Error State -->
                    <div v-else-if="errorMessage" class="p-4 bg-red-50 font-sans border border-red-200 rounded-md mb-4">
                        <p class="text-red-700">{{ errorMessage }}</p>
                    </div>

                    <!-- Card Grid -->
                    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div
                            v-for="card in filteredCards"
                            :key="card.title"
                            :ref="el => setCardRef(el, card.title)"
                            :class="[
                                'bg-white rounded-[12px] p-6 shadow-sm flex flex-col gap-4',
                                highlightedCard === normalizeKey(card.title) ? 'highlight-animate' : ''
                            ]"
                        >
                            <!-- Status Badge -->
                            <div class="flex items-center justify-center">
                                <span :class="[
                                    'px-3 py-1 rounded-full text-[12px] font-semibold',
                                    card.status === 'Complete' ? 'bg-[#00423D] text-white' : card.status === 'Not Ready' ? 'bg-[#EDEFEE] text-[#414141]' : 'bg-[#E25C5C] text-white'
                                ]">
                                    {{ card.status }}
                                </span>
                            </div>
                            <!-- Title & Description -->
                            <div>
                                <div :class="[
                                    'font-minion font-semibold text-[25px] leading-[93%] tracking-[-0%] text-center mb-2',
                                    card.status === 'Not Ready' ? 'text-[#A9A9A9]' : 'text-[#00423D]'
                                ]">{{ card.title }}</div>
                                <div class="font-sans font-normal text-[14px] leading-[139%] tracking-[-0%] text-[#414141] text-center mb-2">{{ card.description }}</div>
                            </div>
                            <!-- Actions -->
                            <div class="flex gap-4 mt-auto border-t border-[#E0E0E04A] -mx-6 px-6 -mb-2 relative">
                                <!-- Case for Support: Dropdown Download -->
                                <template v-if="card.title === 'Case for Support'">
                                    <button
                                        :disabled="card.status === 'Not Ready'"
                                        @click="handleDropdownToggle(card)"
                                        :class="[
                                            'flex-1 flex items-center mt-3 gap-2 justify-center bg-white font-sans text-[14px] leading-[160%] tracking-[-0%] relative',
                                            card.status === 'Not Ready' 
                                                ? 'text-[#9CA3AF] cursor-not-allowed opacity-60' 
                                                : 'text-[#414141] hover:text-[#00423D]'
                                        ]"
                                    >
                                        <img src="@/assets/download_icon.svg" alt="Download" class="w-4 h-4" :class="{ 'opacity-60': card.status === 'Not Ready' }" />
                                        Download
                                    </button>
                                    <!-- Dropdown -->
                                    <div v-if="showDropdown" class="absolute z-20 bg-white border border-gray-200 rounded-lg shadow w-44 right-0 top-12">
                                        <ul class="py-2 text-sm text-gray-700">
                                            <li>
                                                <a href="#" @click.prevent="downloadCaseForSupportPDF" class="block px-4 py-2 hover:bg-gray-100">PDF File</a>
                                            </li>
                                            <li>
                                                <a href="#" @click.prevent="downloadCaseForSupportWord" class="block px-4 py-2 hover:bg-gray-100">Word File</a>
                                            </li>
                                        </ul>
                                    </div>
                                </template>
                                <template v-else-if="card.title === 'Campaign Gift Pyramid'">
                                    <button
                                        :disabled="card.status !== 'Complete'"
                                        @click="exportGiftPyramidExcel"
                                        :class="[
                                            'flex-1 flex items-center mt-3 gap-2 justify-center bg-white font-sans text-[14px] leading-[160%] tracking-[-0%]',
                                            card.status !== 'Complete' 
                                                ? 'text-[#9CA3AF] cursor-not-allowed opacity-60' 
                                                : 'text-[#414141] hover:text-[#00423D]'
                                        ]"
                                    >
                                        <img src="@/assets/download_icon.svg" alt="Download" class="w-4 h-4" :class="{ 'opacity-60': card.status !== 'Complete' }" />
                                        Download
                                    </button>
                                </template>
                                <template v-else-if="card.title === 'Stewardship Structure'">
                                    <button
                                        :disabled="card.status !== 'Complete'"
                                        @click="downloadStewardshipStructureCard(card)"
                                        :class="[
                                            'flex-1 flex items-center mt-3 gap-2 justify-center bg-white font-sans text-[14px] leading-[160%] tracking-[-0%]',
                                            card.status !== 'Complete' 
                                                ? 'text-[#9CA3AF] cursor-not-allowed opacity-60' 
                                                : 'text-[#414141] hover:text-[#00423D]'
                                        ]"
                                    >
                                        <img src="@/assets/download_icon.svg" alt="Download" class="w-4 h-4" :class="{ 'opacity-60': card.status !== 'Complete' }" />
                                        Download
                                    </button>
                                </template>
                                <template v-else-if="card.downloadUrl">
                                    <button 
                                        :disabled="card.status === 'Not Ready'"
                                        @click="downloadFile(card.downloadUrl, card.fileName)"
                                        :class="[
                                            'flex-1 flex items-center mt-3 gap-2 justify-center bg-white font-sans text-[14px] leading-[160%] tracking-[-0%]',
                                            card.status === 'Not Ready' 
                                                ? 'text-[#9CA3AF] cursor-not-allowed opacity-60' 
                                                : 'text-[#414141] hover:text-[#00423D]'
                                        ]"
                                    >
                                        <img src="@/assets/download_icon.svg" alt="Download" class="w-4 h-4" :class="{ 'opacity-60': card.status === 'Not Ready' }" />
                                        Download
                                    </button>
                                </template>
                                <template v-else-if="card.title === 'Talking Points'">
                                    <button
                                        :disabled="card.status !== 'Complete'"
                                        @click="downloadTalkingPointsPDF(card)"
                                        :class="[
                                            'flex-1 flex items-center mt-3 gap-2 justify-center bg-white font-sans text-[14px] leading-[160%] tracking-[-0%]',
                                            card.status !== 'Complete' 
                                                ? 'text-[#9CA3AF] cursor-not-allowed opacity-60' 
                                                : 'text-[#414141] hover:text-[#00423D]'
                                        ]"
                                    >
                                        <img src="@/assets/download_icon.svg" alt="Download" class="w-4 h-4" :class="{ 'opacity-60': card.status !== 'Complete' }" />
                                        Download
                                    </button>
                                </template>
                                <template v-else-if="card.title === 'Understanding Your Fundraising Goals'">
                                    <button
                                        :disabled="card.status !== 'Complete'"
                                        @click="downloadFundingSummaryPDF(card)"
                                        :class="[
                                            'flex-1 flex items-center mt-3 gap-2 justify-center bg-white font-sans text-[14px] leading-[160%] tracking-[-0%]',
                                            card.status !== 'Complete' 
                                                ? 'text-[#9CA3AF] cursor-not-allowed opacity-60' 
                                                : 'text-[#414141] hover:text-[#00423D]'
                                        ]"
                                    >
                                        <img src="@/assets/download_icon.svg" alt="Download" class="w-4 h-4" :class="{ 'opacity-60': card.status !== 'Complete' }" />
                                        Download
                                    </button>
                                </template>
                                <template v-else-if="card.title === 'Case for Support Narrative'">
                                    <button
                                        :disabled="card.status === 'Not Ready'"
                                        @click="downloadCaseForSupportNarrativeWord"
                                        :class="[
                                            'flex-1 flex items-center mt-3 gap-2 justify-center bg-white font-sans text-[14px] leading-[160%] tracking-[-0%]',
                                            card.status === 'Not Ready' 
                                                ? 'text-[#9CA3AF] cursor-not-allowed opacity-60' 
                                                : 'text-[#414141] hover:text-[#00423D]'
                                        ]"
                                    >
                                        <img src="@/assets/download_icon.svg" alt="Download" class="w-4 h-4" :class="{ 'opacity-60': card.status === 'Not Ready' }" />
                                        Download
                                    </button>
                                </template>
                                <template v-else-if="card.title === 'Policies and Procedures'">
                                    <button
                                        :disabled="card.status !== 'Complete'"
                                        @click="downloadPoliciesAndProceduresPDF(card)"
                                        :class="[
                                            'flex-1 flex items-center mt-3 gap-2 justify-center bg-white font-sans text-[14px] leading-[160%] tracking-[-0%]',
                                            card.status !== 'Complete' 
                                                ? 'text-[#9CA3AF] cursor-not-allowed opacity-60' 
                                                : 'text-[#414141] hover:text-[#00423D]'
                                        ]"
                                    >
                                        <img src="@/assets/download_icon.svg" alt="Download" class="w-4 h-4" :class="{ 'opacity-60': card.status !== 'Complete' }" />
                                        Download
                                    </button>
                                </template>
                                <template v-else>
                                    <button 
                                        :disabled="card.status === 'Not Ready'"
                                        :class="[
                                            'flex-1 flex items-center mt-3 gap-2 justify-center bg-white font-sans text-[14px] leading-[160%] tracking-[-0%]',
                                            card.status === 'Not Ready' 
                                                ? 'text-[#9CA3AF] cursor-not-allowed opacity-60' 
                                                : 'text-[#414141] hover:text-[#00423D]'
                                        ]"
                                    >
                                        <img src="@/assets/download_icon.svg" alt="Download" class="w-4 h-4" :class="{ 'opacity-60': card.status === 'Not Ready' }" />
                                        Download
                                    </button>
                                </template>
                            </div>
                        </div>
                    </div>
                    <!-- Loading Overlay for PDF -->
                    <div v-if="isLoadingPdf" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 animate-fade-in">
                        <div class="w-full h-full flex items-center justify-center">
                            <div class="text-center">
                                <div class="mb-6">
                                    <ActivityIndicator />
                                </div>
                                <div class="text-white">
                                    <h3 class="text-xl font-semibold mb-3 font-sans">{{ loadingMessage }}</h3>
                                    <p class="text-sm opacity-90 font-sans">Please wait while we generate your document...</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick, onUnmounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import global from "@/global";
import { useRoute } from 'vue-router';
import TraditionalStyleThemeMain from '@/components/Themes/Main/Traditional.vue';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import ActivityIndicator from '@/components/ActivityIndicator.vue';
import * as XLSX from 'xlsx';

const selectedTab = ref('All');
const tabs = ['All', 'Donor Materials', 'Internal Operations', 'Gift Documentation'];
const isLoading = ref(true);
const errorMessage = ref("");
const cards = ref([]);
const authStore = useAuthStore();
const route = useRoute();
const highlightedCard = ref(null);
const cardRefs = ref({});
const showDropdown = ref(false); // For Case for Support download dropdown
const isLoadingPdf = ref(false); // For loading overlay
const caseForSupportRef = ref(null); // For PDF generation
const loadingMessage = ref(''); // For custom loading messages

function normalizeKey(str) {
  // Remove any special characters and convert to lowercase
  return (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

const filteredCards = computed(() => {
    if (selectedTab.value === 'All') return cards.value;
    return cards.value.filter(card => card.type === selectedTab.value);
});

// Update the ref handling in the template
const setCardRef = (el, title) => {
  if (el) {
    const key = normalizeKey(title);
    if (!cardRefs.value) {
      cardRefs.value = {};
    }
    cardRefs.value[key] = el;
  }
};

onMounted(async () => {
    try {
        isLoading.value = true;
        errorMessage.value = "";
        await authStore.fetchUserData();
        if (!authStore.user?.accessToken) {
            throw new Error('Unable to load user authentication. Please try refreshing the page.');
        }
        
        // Handle tab parameter from URL query
        if (route.query.tab && tabs.includes(route.query.tab)) {
            selectedTab.value = route.query.tab;
        }
        
        // Add click outside handler for dropdown
        document.addEventListener('click', handleClickOutside);
        
        const res = await fetch(`${global.API_URL}/fundraising-collateral-status`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
            },
        });
        
        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }
        
        const data = await res.json();
        const collateralStatus = data.fundraising_collateral_status;

        // Map API response to cards structure
        cards.value = [
            // Donor Materials
            { 
                status: collateralStatus["Case for Support"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Case for Support', 
                description: 'A comprehensive document that articulates your organization\'s mission, impact, and funding needs.', 
                type: 'Donor Materials' 
            },
            { 
                status: collateralStatus["Stewardship Structure"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Stewardship Structure', 
                description: 'A framework for managing donor relationships and ensuring proper recognition and engagement.', 
                type: 'Donor Materials' 
            },
            
            // Internal Operations
            { 
                status: collateralStatus["Case for Support Narrative"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Case for Support Narrative', 
                description: 'A detailed narrative that supports your case for funding and organizational impact.', 
                type: 'Internal Operations' 
            },
            { 
                status: collateralStatus["Understanding Your Fundraising Goals"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Understanding Your Fundraising Goals', 
                description: 'A strategic document outlining your organization\'s fundraising objectives and targets.', 
                type: 'Internal Operations' 
            },
            { 
                status: collateralStatus["Talking Points"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Talking Points', 
                description: 'Key messages and talking points for fundraising conversations and presentations.', 
                type: 'Internal Operations' 
            },
            { 
                status: collateralStatus["Campaign Gift Pyramid"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Campaign Gift Pyramid', 
                description: 'A visual representation of your fundraising campaign\'s gift structure and targets.', 
                type: 'Internal Operations' 
            },
            { 
                status: collateralStatus["Policies and Procedures"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Policies and Procedures', 
                description: 'Comprehensive guidelines for managing donations, gifts, and fundraising activities.', 
                type: 'Internal Operations' 
            },
            { 
                status: collateralStatus["Gift Acceptance Policy"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Gift Acceptance Policy', 
                description: 'This policy specifies what types of gifts are accepted so that staff can narrow their focus to only those donations that further their mission.', 
                type: 'Internal Operations',
                downloadUrl: '/files/GiftAcceptancePolicy.docx',
                fileName: 'Gift Acceptance Policy.docx'
            },
            { 
                status: collateralStatus["Gift-Entry and Recording Policy"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Gift-Entry and Recording Policy', 
                description: 'This policy, which also includes a Cash Handling Policy, outlines the steps in which donations should be received, recorded, deposited, acknowledged, and reported.', 
                type: 'Internal Operations',
                downloadUrl: '/files/GiftEntryAndRecordingPolicy.docx',
                fileName: 'Gift Entry and Recording Policy.docx'
            },
            { 
                status: collateralStatus["Gift Acknowledgement Policy"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Gift Acknowledgement Policy', 
                description: 'This policy, which also includes a Pledge Reminder Policy, further details exactly how gifts and pledges should be acknowledged and in what time frame.', 
                type: 'Internal Operations',
                downloadUrl: '/files/GiftAcknowledgmentPolicy.docx',
                fileName: 'Gift Acknowledgment Policy.docx'
            },
            { 
                status: collateralStatus["Naming Rights Policy"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Naming Rights Policy', 
                description: 'This policy illustrates what types of gifts can be considered for naming rights and highlights the associated parameters to which staff must adhere.', 
                type: 'Internal Operations',
                downloadUrl: '/files/NamingGiftsPolicy.docx',
                fileName: 'Naming Gifts Policy.docx'
            },
            { 
                status: collateralStatus["Corporate Support Policy"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Corporate Support Policy', 
                description: 'This policy lists the requirements that an organization must consider when seeking support from a corporate entity.', 
                type: 'Internal Operations',
                downloadUrl: '/files/CorporateSupportPolicy.docx',
                fileName: 'Corporate Support Policy.docx'
            },
            { 
                status: collateralStatus["Donor Privacy Policy"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Donor Privacy Policy', 
                description: 'This policy highlights the expectations of staff and board members (both current and former) when dealing with donors sensitive information, and details how, when, and where this information should be used', 
                type: 'Internal Operations',
                downloadUrl: '/files/DonorPrivacyPolicy.docx',
                fileName: 'Donor Privacy Policy.docx'
            },
            
            // Gift Documentation
            { 
                status: collateralStatus["Gift Agreement: Pledge"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Gift Agreement: Pledge', 
                downloadUrl: '/files/GiftAgreementForAnOutrightGift.docx',
                fileName: 'Gift Agreement For An Outright Gift.docx',
                description: 'Standard template for documenting donor pledges and commitments.', 
                type: 'Gift Documentation' 
            },
            { 
                status: collateralStatus["Gift Agreement: Planned Gift"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Gift Agreement: Planned Gift',
                downloadUrl: '/files/GiftAgreementForNamingAnEndowment.docx',
                fileName: 'Gift Agreement For Naming an Endowment.docx',
                description: 'Template for documenting planned giving arrangements and bequests.', 
                type: 'Gift Documentation' 
            },
            { 
                status: collateralStatus["Pledge Form"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Pledge Form', 
                description: 'Standard form for donors to make and document their pledges.', 
                type: 'Gift Documentation',
                downloadUrl: '/files/PledgeForm.docx',
                fileName: 'Pledge Form.docx'
            },
            { 
                status: collateralStatus["Stock and Wire Transfer Instructions"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Stock and Wire Transfer Instructions', 
                description: 'Detailed instructions for processing stock donations and wire transfers.', 
                type: 'Gift Documentation',
                downloadUrl: '/files/StockAndWireTransferInstructions.docx',
                fileName: 'Stock and Wire Transfer Instructions.docx'
            },
            { 
                status: collateralStatus["Standard Tax Receipt and Acknowledgement Letter"]?.completed ? 'Complete' : 'Not Ready', 
                title: 'Standard Tax Receipt and Acknowledgement Letter', 
                description: 'Template for providing donors with tax documentation and acknowledgments.', 
                type: 'Gift Documentation',
                downloadUrl: '/files/DonorAcknowledgementLetter.docx',
                fileName: 'Donor Acknowledgement Letter.docx'
            }
        ];

        // Wait for cards to be rendered
        await nextTick();
        
        // Handle initial highlight if present
        if (route.query.highlight) {
            handleHighlight(route.query.highlight);
        }
    } catch (error) {
        errorMessage.value = error.message || 'Failed to load fundraising collateral status.';
        console.error(error);
    } finally {
        isLoading.value = false;
    }
});

// Function to handle highlighting and scrolling
const handleHighlight = async (highlightValue) => {
    try {
        // Clear any existing highlight
        highlightedCard.value = '';
        await nextTick();
        
        // Set the new highlight with normalized key
        const normalizedKey = normalizeKey(highlightValue);
        highlightedCard.value = normalizedKey;
        
        // Wait for cards to be rendered
        await nextTick();
        
        // Find the card and scroll container
        const scrollContainer = document.querySelector('.right-col-content');
        const cardElement = cardRefs.value[normalizedKey];
        
        if (!scrollContainer || !cardElement) {
            console.warn('Required elements not found:', { scrollContainer: !!scrollContainer, cardElement: !!cardElement });
            return;
        }

        // Function to perform the scroll
        const performScroll = () => {
            const containerHeight = scrollContainer.clientHeight;
            const cardHeight = cardElement.offsetHeight;
            const cardTop = cardElement.offsetTop;
            const currentScroll = scrollContainer.scrollTop;
            const containerTop = scrollContainer.offsetTop;
            
            // Calculate the visible boundaries of the scroll container
            const containerVisibleTop = currentScroll;
            const containerVisibleBottom = currentScroll + containerHeight;
            
            // Check if card is already fully visible
            const cardTopRelative = cardTop;
            const cardBottomRelative = cardTop + cardHeight;
            
            let newScrollPosition = currentScroll;
            
            // If card is above visible area
            if (cardTopRelative < containerVisibleTop) {
                newScrollPosition = cardTopRelative;
            }
            // If card is below visible area
            else if (cardBottomRelative > containerVisibleBottom) {
                newScrollPosition = cardBottomRelative - containerHeight;
            }
            
            // Add some padding to ensure the card isn't right at the edge
            const padding = 20;
            newScrollPosition = Math.max(0, newScrollPosition - padding);
            
            // Only scroll if needed
            if (newScrollPosition !== currentScroll) {
                scrollContainer.scrollTo({
                    top: newScrollPosition,
                    behavior: 'smooth'
                });
            }
        };

        // Try scrolling multiple times to ensure it works
        setTimeout(performScroll, 100);  // Initial attempt
        setTimeout(performScroll, 300);  // Backup attempt
        setTimeout(performScroll, 500);  // Final attempt
        
        // Clear highlight after animation
        setTimeout(() => {
            highlightedCard.value = '';
        }, 10000);
    } catch (error) {
        console.error('Error during highlight handling:', error);
    }
};

// Update the watch to use the new handler
watch(
    () => route.query.highlight,
    (newHighlight) => {
        if (newHighlight) {
            handleHighlight(newHighlight);
        }
    },
    { immediate: true }
);

// Update the filteredCards watch
watch(filteredCards, async () => {
    await nextTick();
    if (route.query.highlight) {
        handleHighlight(route.query.highlight);
    }
}, { deep: true });

// Add cleanup on unmount
onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});

// Handle click outside dropdown
function handleClickOutside(event) {
    const dropdown = document.querySelector('.dropdown-container');
    if (dropdown && !dropdown.contains(event.target)) {
        showDropdown.value = false;
    }
}

// Helper function to show loading overlay
function showLoadingOverlay(message = 'Generating your document...') {
  isLoadingPdf.value = true;
  loadingMessage.value = message;
}

// Helper function to hide loading overlay
function hideLoadingOverlay() {
  isLoadingPdf.value = false;
  loadingMessage.value = '';
}

async function downloadCaseForSupportPDF() {
  showLoadingOverlay('Generating Case for Support PDF...');
  await nextTick();
  // Add a small delay to ensure rendering
  await new Promise(resolve => setTimeout(resolve, 300));
  const el = caseForSupportRef.value;
  if (!el) {
    hideLoadingOverlay();
    return;
  }
  const doc = new jsPDF({
    orientation: 'p',
    unit: 'px',
    format: 'a4',
    putOnlyUsedFonts: true,
    floatPrecision: 'smart',
  });
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  // Multi-page logic: find all .theme-page-TraditionalStyle elements
  const pages = [];
  const pageNodes = el.querySelectorAll('.theme-page-TraditionalStyle');
  for (const page of pageNodes) {
    // Ensure the page is visible for rendering
    page.classList.add('!block');
    // Wait for images to load (optional: can be improved)
    const images = page.querySelectorAll('img');
    const imagePromises = Array.from(images).map(img => {
      return new Promise(resolve => {
        if (img.complete) resolve();
        else img.onload = resolve;
      });
    });
    await Promise.all(imagePromises);
    // Render to canvas
    const canvas = await html2canvas(page, {
      scale: 1.75,
      removeContainer: false,
      allowTaint: true,
      useCORS: true,
    });
    const imgData = canvas.toDataURL('image/png', 1.0);
    pages.push(imgData);
  }
  // Add each page to the PDF
  pages.forEach((imgData, idx) => {
    doc.addImage(imgData, 'PNG', 0, 0, width, height);
    if (idx < pages.length - 1) {
      doc.addPage();
    }
  });
  // Clean up: remove !block class
  for (const page of pageNodes) {
    page.classList.remove('!block');
  }
  doc.save('Case for Support.pdf');
  hideLoadingOverlay();
  showDropdown.value = false;
}

async function downloadCaseForSupportWord() {
  showLoadingOverlay('Generating Case for Support Word document...');
  try {
    const authStore = useAuthStore();
    const res = await fetch(`${global.API_URL}/create_docx`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) throw new Error('Network response was not ok');
    const blob = await res.blob();
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    link.href = url;
    link.download = 'Case for Support.docx';
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    showDropdown.value = false;
  } catch (error) {
    console.error('Download error:', error);
  } finally {
    hideLoadingOverlay();
  }
}

function handleDropdownToggle(card) {
  if (card.title === 'Case for Support' && card.status === 'Complete') {
    showDropdown.value = !showDropdown.value;
  }
}

function downloadFile(url, fileName) {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function formatToUSD(value) {
  if (typeof value === 'string' && (value === 'Grand Total' || value.includes('Various'))) return value;
  if (isNaN(value)) return value;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

async function fetchGiftPyramidData() {
  try {
    const res = await fetch(`${global.API_URL}/gift-pyramid`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      const text = await res.text();
      console.error('Gift pyramid API error:', res.status, text);
      throw new Error(`API error: ${res.status}`);
    }
    const data = await res.json();
    console.log('Gift pyramid API response:', data);
    if (!data.data || !data.data.levels) {
      throw new Error('No gift pyramid data found in API response');
    }
    return JSON.parse(data.data.levels);
  } catch (err) {
    console.error('Failed to fetch gift pyramid data:', err);
    return [];
  }
}

async function exportGiftPyramidExcel() {
  showLoadingOverlay('Generating Gift Pyramid Excel file...');
  try {
    const tableData = await fetchGiftPyramidData();
    if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
      console.error('No table data to export:', tableData);
      hideLoadingOverlay();
      return;
    }
    let xlsxData = [...tableData];
    // Add Grand Total row
    xlsxData.push({
      giftAmount: 'Grand Total',
      numberOfGiftsNeeded: tableData.reduce((acc, curr) => {
        if (curr.numberOfGiftsNeeded != 'Many' && curr.numberOfGiftsNeeded != '-') {
          return acc + parseInt(curr.numberOfGiftsNeeded);
        }
        return acc + 0;
      }, 0),
      numberOfProspectsNeeded: tableData.reduce((acc, curr) => {
        if (curr.numberOfProspectsNeeded != 'Many' && curr.numberOfProspectsNeeded != '-') {
          return acc + parseInt(curr.numberOfProspectsNeeded);
        }
        return acc + 0;
      }, 0),
      prospectNames: '',
      amountByGiftLevel: tableData[tableData.length-1]?.cumulativeGiftAmount || '',
      cumulativeGiftAmount: tableData[tableData.length-1]?.cumulativeGiftAmount || '',
      fundingAllocation: '100%'
    });
    const orderedXlsxData = xlsxData.map((ele) => ({
      'Gift Amount': ele.giftAmount == 'Grand Total' || (typeof ele.giftAmount === 'string' && ele.giftAmount.includes('Various')) ? ele.giftAmount : formatToUSD(ele.giftAmount),
      'Number Of Gifts Needed': ele.numberOfGiftsNeeded,
      'Number Of Prospects Needed': ele.numberOfProspectsNeeded,
      'Prospect Names': '',
      'Amount By GiftLevel': formatToUSD(ele.amountByGiftLevel),
      'Cumulative Gift Amount': formatToUSD(ele.cumulativeGiftAmount),
      'Funding Allocation': typeof ele.fundingAllocation === 'string' ? ele.fundingAllocation : `${parseFloat(ele.fundingAllocation).toFixed(2)}%`
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(orderedXlsxData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Gift Pyramid');
    XLSX.writeFile(workbook, 'Gift Pyramid.xlsx');
  } catch (err) {
    console.error('Failed to export Excel:', err);
  } finally {
    hideLoadingOverlay();
  }
}

async function downloadStewardshipStructureCard(card) {
  showLoadingOverlay('Generating Stewardship Structure PDF...');
  let tempContainer = null;
  try {
    // Fetch stewardship data from the same endpoint as DonorRecognitionAndBenefitStructureView.vue
    const res = await fetch(`${global.API_URL}/stewardships`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }
    const answer = await res.json();
    let rows = [];
    if (answer.stewardships && answer.stewardships.length > 0) {
      rows = answer.stewardships.map(ss => ss.benefit);
    }
    if (rows.length === 0) {
      // fallback: one empty row
      rows = [{
        benefit_name: '',
        prospect: {
          1000000: false,
          500000: false,
          250000: false,
          100000: false,
          25000: false,
          'Various (<$25k)': false,
        },
      }];
    }

    // Table columns as in DonorRecognitionAndBenefitStructureView.vue
    const columns = [
      { label: 'Donor Benefit', key: 'benefit_name' },
      { label: '$ 1,000,000', key: 1000000 },
      { label: '$ 500,000', key: 500000 },
      { label: '$ 250,000', key: 250000 },
      { label: '$ 100,000', key: 100000 },
      { label: '$ 25,000', key: 25000 },
      { label: 'Various (<$25k)', key: 'Various (<$25k)' },
    ];

    // Generate table header
    const tableHeader = `
      <tr style="background-color: #00423D; color: #fff;">
        ${columns.map(col => `<th style='border:1px solid #ccc; padding:8px; font-weight:bold;'>${col.label}</th>`).join('')}
      </tr>
    `;

    // Generate table rows
    const tableRows = rows.map(row => `
      <tr>
        <td style='border:1px solid #ccc; padding:8px;'>${row.benefit_name || ''}</td>
        ${columns.slice(1).map(col => {
          const checked = row.prospect && row.prospect[col.key];
          return `<td style='border:1px solid #ccc; padding:8px; text-align:center;'>${checked ? '✓' : ''}</td>`;
        }).join('')}
      </tr>
    `).join('');

    // Create a temporary container for the complete stewardship structure
    tempContainer = document.createElement('div');
    tempContainer.style.width = '1000px';
    tempContainer.style.padding = '40px';
    tempContainer.style.backgroundColor = 'white';
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';

    tempContainer.innerHTML = `
      <div style="font-family: sans-serif; color: #414141;">
        <h2 style="color: #00423D; font-size: 24px; margin-bottom: 20px;">Donor Recognition and Benefit Structure</h2>
        <div style="font-size: 14px; line-height: 1.5; margin-bottom: 12px;">Offering benefits and recognition to your donors is a great way to steward them after they have made gifts to your organization. The more your benefits and recognition integrate your donors into the work of your organization, the better. More intimate and exclusive benefits should be reserved for higher level donors; More accessible and cost effective benefits should be reserved for smaller level donors. The objective is to keep donors engaged with your organization's work and progress.</div>
        <div style="font-size: 14px; line-height: 1.5; margin-bottom: 12px;">Examples of benefits and recognition include: Private dinners with organizational leaders, field trips, naming opportunities, newsletters, annual report listings, etc.</div>
        <div style="font-size: 14px; line-height: 1.5; margin-bottom: 18px; margin-top: 20px;"><span style="font-weight: bold; color: #00423D;">Instructions:</span> Below, you will find a framework to help you build out your organization's Stewardship structure. In the left column, insert the benefit or recognition you will offer your donors at various the gift levels listed in the top row. Then, click in the corresponding box under the gift level that will receive each benefit or recognition. Your final stewardship chart will be inserted into your case for support.</div>
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
          <thead>${tableHeader}</thead>
          <tbody>${tableRows}</tbody>
        </table>
      </div>
    `;

    // Add the container to the document
    document.body.appendChild(tempContainer);
    await nextTick();
    // Use html2canvas to capture the content
    const canvas = await html2canvas(tempContainer, {
      backgroundColor: '#ffffff',
      useCORS: true,
      scale: 2,
      logging: false,
      width: 1000,
      height: tempContainer.offsetHeight,
      allowTaint: true,
      removeContainer: false
    });

    // Create PDF
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    // Calculate image dimensions to fit page
    let imgWidth = pageWidth - 40;
    let imgHeight = (canvas.height * imgWidth) / canvas.width;
    if (imgHeight > pageHeight - 40) {
      imgHeight = pageHeight - 40;
      imgWidth = (canvas.width * imgHeight) / canvas.height;
    }
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 20, 20, imgWidth, imgHeight);
    pdf.save('Donor-Recognition-and-Benefit-Structure.pdf');
  } catch (error) {
    console.error('Failed to generate stewardship structure:', error);
  } finally {
    if (tempContainer && tempContainer.parentNode) {
      document.body.removeChild(tempContainer);
    }
    hideLoadingOverlay();
  }
}

async function downloadTalkingPointsPDF(card) {
  showLoadingOverlay('Generating Talking Points PDF...');
  let tempContainer = null;
  try {
    // Fetch pitch training data
    const res = await fetch(`${global.API_URL}/pitch-training`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }
    const data = await res.json();
    const info = data.data || {};

    // Format number helper
    function formatNumber(value) {
      if (!value) return '0';
      return new Intl.NumberFormat('en-US').format(value);
    }

    // Create a temporary container for the talking points content
    tempContainer = document.createElement('div');
    tempContainer.style.width = '1000px';
    tempContainer.style.padding = '40px';
    tempContainer.style.backgroundColor = 'white';
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';
    tempContainer.innerHTML = `
      <div style="font-family: sans-serif; color: #414141;">
        <h2 style="color: #00423D; font-size: 24px; margin-bottom: 20px;">Fundraising Talking Points</h2>
        <div style="font-size: 15px; line-height: 1.7;">
          <div style="margin-bottom: 10px;"><b>1.</b> Thank Donor for Meeting</div>
          <div style="margin-bottom: 10px;"><b>2. Mission:</b> ${info.mission || ''}</div>
          <div style="margin-bottom: 10px;"><b>3. Aspirational Vision:</b> ${info.vision || ''}</div>
          <div style="margin-bottom: 10px;"><b>4. Annual Operating Budget:</b> $${formatNumber(info.organization_budget)}</div>
          <div style="margin-bottom: 10px;"><b>5. Annual Philanthropic Revenue:</b> $${formatNumber(info.philanthropic_total)}</div>
          <div style="margin-bottom: 10px;"><b>6. Core Annual Programs:</b>
            <ol type="a" style="margin-left: 20px;">
              ${(Array.isArray(info.core_programs) ? info.core_programs.map((p, i) => `<li>${String.fromCharCode(97 + i)}. ${p?.program_name || ''}</li>`).join('') : '')}
            </ol>
          </div>
          <div style="margin-bottom: 10px;"><b>7. Funding New Projects:</b>
            <ol type="a" style="margin-left: 20px;">
              ${(Array.isArray(info.projects) ? info.projects.map((p, i) => `<li>${String.fromCharCode(97 + i)}. ${p?.project_name || ''}</li>`).join('') : '')}
            </ol>
          </div>
          <div style="margin-bottom: 10px;"><b>8. 3 Year Fundraising Goal:</b> $${formatNumber(info.total_fundraising_goal_3y)}</div>
          ${info.recognition_range !== null && info.recognition_range !== undefined ? `<div style='margin-bottom: 10px;'><b>9. Recognition Range:</b> Up to $${formatNumber(info.recognition_range)}</div>` : ''}
          <div style="margin-bottom: 10px;"><b>10. Solicitation:</b>
            <ol type="a" style="margin-left: 20px;">
              <li>a. How do you want the donor/prospect to support your organization?</li>
              <li>b. What is the specific ask?</li>
            </ol>
          </div>
          <div style="margin-bottom: 10px;"><b>11. Gifts Accepted:</b>
            <ol type="a" style="margin-left: 20px;">
              ${(Array.isArray(info.ways_to_give) ? info.ways_to_give.filter(obj => obj.isSelected === true).map(obj => `<li>${obj.label}</li>`).join('') : '')}
              ${(Array.isArray(info.ways_to_give) && info.ways_to_give[3]?.subOption ? info.ways_to_give[3].subOption.filter(obj => obj.isSelected === true).map(obj => `<li>Planned Gifts - ${obj.label}</li>`).join('') : '')}
            </ol>
          </div>
          <div style="margin-bottom: 10px;"><b>12.</b> Thank you for Meeting</div>
        </div>
      </div>
    `;
    document.body.appendChild(tempContainer);
    await nextTick();
    const canvas = await html2canvas(tempContainer, {
      backgroundColor: '#ffffff',
      useCORS: true,
      scale: 2,
      logging: false,
      width: 1000,
      height: tempContainer.offsetHeight,
      allowTaint: true,
      removeContainer: false
    });
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let imgWidth = pageWidth - 40;
    let imgHeight = (canvas.height * imgWidth) / canvas.width;
    if (imgHeight > pageHeight - 40) {
      imgHeight = pageHeight - 40;
      imgWidth = (canvas.width * imgHeight) / canvas.height;
    }
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 20, 20, imgWidth, imgHeight);
    pdf.save('Fundraising-Talking-Points.pdf');
  } catch (error) {
    console.error('Failed to generate talking points PDF:', error);
  } finally {
    if (tempContainer && tempContainer.parentNode) {
      document.body.removeChild(tempContainer);
    }
    hideLoadingOverlay();
  }
}

async function downloadFundingSummaryPDF(card) {
  showLoadingOverlay('Generating Funding Summary PDF...');
  let tempContainer = null;
  try {
    // Fetch funding summary data from the API
    const res = await fetch(`${global.API_URL}/total_fundraising_goal/total_fundraising_goal_for_3_years`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }
    const data = await res.json();
    
    // Map API response to fundingData with null/undefined handling
    const fundingData = {
      annual_operating_expenses_year_1: data.annual_operating_expenses_year_1 || 0,
      year_1_annual_fundraising_goal: data.year_1_annual_fundraising_goal || 0,
      year_2_annual_fundraising_goal: data.year_2_annual_fundraising_goal || 0,
      year_3_annual_fundraising_goal: data.year_3_annual_fundraising_goal || 0,
      total_annual_fundraising_goal_for_3_years: data.total_annual_fundraising_goal_for_3_years || 0,
      new_project_expenses_for_3_years: data.new_project_expenses_for_3_years || 0,
      year_1_new_project_fundraising_goal: data.year_1_new_project_fundraising_goal || 0,
      year_2_new_project_fundraising_goal: data.year_2_new_project_fundraising_goal || 0,
      year_3_new_project_fundraising_goal: data.year_3_new_project_fundraising_goal || 0,
      total_new_project_fundraising_goal_for_3_years: data.total_new_project_fundraising_goal_for_3_years || 0,
      final_total_fundraising_goal_for_3_years: data.final_total_fundraising_goal_for_3_years || 0
    };

    // Fetch skipped modules data to determine if project modules are skipped
    const skippedModulesRes = await fetch(`${global.API_URL}/extensions/skipped_modules`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authStore.user.accessToken}`,
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store'
      }
    });
    
    let areProjectModulesSkipped = false;
    if (skippedModulesRes.ok) {
      const skippedModulesData = await skippedModulesRes.json();
      
      // Parse skipped routes from API response
      let skippedRoutes = [];
      if (skippedModulesData?.data?.skipped_routes && Array.isArray(skippedModulesData.data.skipped_routes)) {
        skippedRoutes = skippedModulesData.data.skipped_routes;
      } else if (skippedModulesData?.skipped_routes && Array.isArray(skippedModulesData.skipped_routes)) {
        skippedRoutes = skippedModulesData.skipped_routes;
      }
      
      // Helper function to check if a route is skipped (same logic as FundingSummaryView.vue)
      const isRouteSkipped = (route, skippedRoutes) => {
        if (!Array.isArray(skippedRoutes) || typeof route !== 'string') {
          return false;
        }
        
        // Special case: Module 9 overview should not be considered skipped
        if (route === '/steps/funding-new-projects/overview') {
          return false;
        }
        
        // Check if the exact route is skipped or if it's part of a skipped module
        return skippedRoutes.some(skippedRoute => {
          // Check for exact match
          if (skippedRoute === route) return true;
          // Check if the current route is part of a skipped module path
          const moduleBase = route.split('/').slice(0, 3).join('/');
          return skippedRoute === moduleBase;
        });
      };
      
      // Check if project module 9 is skipped
      const module9Path = '/steps/funding-new-projects';
      
      areProjectModulesSkipped = isRouteSkipped(module9Path, skippedRoutes);
    }

    // Helper function to format currency
    const formatCurrency = (value) => {
      const numValue = value || 0;
      return numValue.toLocaleString();
    };

    // Create a temporary container for the Funding Summary content
    tempContainer = document.createElement('div');
    tempContainer.style.width = '1000px';
    tempContainer.style.padding = '40px';
    tempContainer.style.backgroundColor = 'white';
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';
    
    // Build the goal rows HTML
    const goalRowsHtml = `<!-- Annual Fundraising Goal Section -->
      <div style="display: flex; align-items: center; padding: 1rem;">
        <div style="display: flex; flex-direction: column; align-items: flex-start; width: 40%;">
          <div style="font-size: 1rem; font-weight: 600; color: #00423D; margin-bottom: 0.75rem;">Annual Fundraising Goal</div>
          <div style="background-color: #E8F5E8; border: 1px solid #00423D; padding: 0.75rem 1.5rem; font-size: 1.25rem; font-weight: 600; color: #00423D; border-radius: 8px; min-width: 200px; width: 200px; display: flex; align-items: center; justify-content: center; padding-bottom: 2rem;">$${formatCurrency(fundingData.year_1_annual_fundraising_goal)}</div>
        </div>
        <div style="width: 60%; display: flex; align-items: center;">
          <div style="display: flex; align-items: center; gap: 1rem;">
            <span style="font-size: 1.5rem; font-weight: 600; color: #00423D;">=</span>
            <span style="font-size: 0.875rem; color: #00423D; line-height: 1.5;">Total Annual Operating Budget (<span style="color: #00423D; font-weight: 600;">Program Expenses</span> + <span style="color: #00423D; font-weight: 600;">Overhead Expenses</span>) – Other Revenue (<span style="color: #00423D; font-weight: 600;">Earned Revenue</span>, <span style="color: #00423D; font-weight: 600;">Investment Income</span>, <span style="color: #00423D; font-weight: 600;">Government Appropriations</span> & <span style="color: #00423D; font-weight: 600;">In-Kind</span>).</span>
          </div>
        </div>
      </div><!-- Annual Fundraising Goal for 3 Years Section -->
      <div style="display: flex; align-items: center; padding: 1rem;">
        <div style="display: flex; flex-direction: column; align-items: flex-start; width: 40%;">
          <div style="font-size: 1rem; font-weight: 600; color: #00423D; margin-bottom: 0.75rem;">Annual Fundraising Goal for 3 Years</div>
          <div style="background-color: #E8F5E8; border: 1px solid #00423D; padding: 0.75rem 1.5rem; font-size: 1.25rem; font-weight: 600; color: #00423D; border-radius: 8px; min-width: 200px; width: 200px; display: flex; align-items: center; justify-content: center; padding-bottom: 2rem;">$${formatCurrency(fundingData.total_annual_fundraising_goal_for_3_years)}</div>
        </div>
        <div style="width: 60%; display: flex; align-items: center;">
          <div style="display: flex; align-items: center; gap: 1rem;">
            <span style="font-size: 1.5rem; font-weight: 600; color: #00423D;">=</span>
            <span style="font-size: 0.875rem; color: #00423D; line-height: 1.5;">Annual Fundraising Goal for three (3) years.</span>
          </div>
        </div>
      </div>${!areProjectModulesSkipped ? `<!-- New Project Goal for 3 Years Section -->
      <div style="display: flex; align-items: center; padding: 1rem;">
        <div style="display: flex; flex-direction: column; align-items: flex-start; width: 40%;">
          <div style="font-size: 2.5rem; font-weight: 600; color: #00423D; margin-top: -2rem; margin-bottom: 0.75rem; text-align: center; width: 200px; margin-left: 0;">+</div>
          <div style="font-size: 1rem; font-weight: 600; color: #00423D; margin-bottom: 0.75rem;">New Project Goal for 3 Years</div>
          <div style="background-color: #E8F5E8; border: 1px solid #00423D; padding: 0.75rem 1.5rem; font-size: 1.25rem; font-weight: 600; color: #00423D; border-radius: 8px; min-width: 200px; width: 200px; display: flex; align-items: center; justify-content: center; padding-bottom: 2rem;">$${formatCurrency(fundingData.total_new_project_fundraising_goal_for_3_years)}</div>
        </div>
        <div style="width: 60%; display: flex; align-items: center;">
          <div style="display: flex; align-items: center; gap: 1rem;">
            <span style="font-size: 1.5rem; font-weight: 600; color: #00423D;">=</span>
            <span style="font-size: 0.875rem; color: #00423D; line-height: 1.5;">This is the sum of your New Project Goals over three years.</span>
          </div>
        </div>
      </div>` : ''}<!-- Total Fundraising Goal for 3 Years Section -->
      <div style="display: flex; align-items: center; padding: 1rem;">
        <div style="display: flex; flex-direction: column; align-items: flex-start; width: 40%;">
          ${!areProjectModulesSkipped ? `<div style="font-size: 2.5rem; font-weight: 600; color: #00423D; margin-top: -2rem; margin-bottom: 0.75rem; text-align: center; width: 200px; margin-left: 0;">=</div>` : ''}
          <div style="font-size: 1rem; font-weight: 600; color: #00423D; margin-bottom: 0.75rem;">Total Fundraising Goal for 3 Years</div>
          <div style="background-color: #E8F5E8; border: 1px solid #00423D; padding: 0.75rem 1.5rem; font-size: 1.25rem; font-weight: 600; color: #00423D; border-radius: 8px; min-width: 200px; width: 200px; display: flex; align-items: center; justify-content: center; padding-bottom: 2rem;">$${formatCurrency(fundingData.final_total_fundraising_goal_for_3_years)}</div>
        </div>
        <div style="width: 60%; display: flex; align-items: center;">
          <div style="display: flex; align-items: center; gap: 1rem;">
            <span style="font-size: 1.5rem; font-weight: 600; color: #00423D;">=</span>
            <span style="font-size: 0.875rem; color: #00423D; line-height: 1.5;">This is the sum of your <span style="color: #00423D; font-weight: 600;">Annual Fundraising Goal</span> and your <span style="color: #00423D; font-weight: 600;">New Project Goal</span> over three years. <span style="font-style: italic; color: #00423D;">*Rounded up to the closest $500K for your gift pyramid.</span></span>
          </div>
        </div>
      </div>`;
    
    tempContainer.innerHTML = `
      <div style="font-family: sans-serif; color: #414141;">
        <h3 style="font-size: 20px; line-height: 28px; margin-bottom: 16px; font-weight: 600; color: #414141;">Summary</h3>
        
        <div style="margin: 0 auto; font-family: sans-serif;">
          ${goalRowsHtml}
        </div>
      </div>
    `;
    document.body.appendChild(tempContainer);
    await nextTick();
    const canvas = await html2canvas(tempContainer, {
      backgroundColor: '#ffffff',
      useCORS: true,
      scale: 2,
      logging: false,
      width: 1000,
      height: tempContainer.offsetHeight,
      allowTaint: true,
      removeContainer: false
    });
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let imgWidth = pageWidth - 40;
    let imgHeight = (canvas.height * imgWidth) / canvas.width;
    if (imgHeight > pageHeight - 40) {
      imgHeight = pageHeight - 40;
      imgWidth = (canvas.width * imgHeight) / canvas.height;
    }
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 20, 20, imgWidth, imgHeight);
    pdf.save('Understanding_Your_Fundraising_Goals.pdf');
  } catch (error) {
    console.error('Failed to generate funding summary PDF:', error);
  } finally {
    if (tempContainer && tempContainer.parentNode) {
      document.body.removeChild(tempContainer);
    }
    hideLoadingOverlay();
  }
}

async function downloadCaseForSupportNarrativeWord() {
  showLoadingOverlay('Generating Case for Support Narrative Word document...');
  try {
    const res = await fetch(`${global.API_URL}/create_docx`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) throw new Error('Network response was not ok');
    const blob = await res.blob();
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    link.href = url;
    link.download = 'Case for Support Narrative.docx';
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Download error:', error);
  } finally {
    hideLoadingOverlay();
  }
}

async function fetchPoliciesForPDF() {
  // Static list of all possible policies
  const allPolicies = [
    {
      identity: "gift_acceptance_policy",
      name: "Gift Acceptance Policy",
      description: "This policy specifies what types of gifts are accepted so that staff can narrow their focus to only those donations that further their mission."
    },
    {
      identity: "gift_entry_and_recording_policy",
      name: "Gift-Entry and Recording Policy",
      description: "This policy, which also includes a Cash Handling Policy, outlines the steps in which donations should be received, recorded, deposited, acknowledged, and reported."
    },
    {
      identity: "gift_acknowledgement_policy",
      name: "Gift Acknowledgement Policy",
      description: "This policy, which also includes a Pledge Reminder Policy, further details exactly how gifts and pledges should be acknowledged and in what time frame."
    },
    {
      identity: "naming_rights_policy",
      name: "Naming Rights Policy",
      description: "This policy illustrates what types of gifts can be considered for naming rights and highlights the associated parameters to which staff must adhere."
    },
    {
      identity: "corporate_support_policy",
      name: "Corporate Support Policy",
      description: "This policy lists the requirements that an organization must consider when seeking support from a corporate entity."
    },
    {
      identity: "donor_privacy_policy",
      name: "Donor Privacy Policy",
      description: "This policy highlights the expectations of staff and board members (both current and former) when dealing with donors' sensitive information, and details how, when, and where this information should be used"
    }
  ];

  try {
    const res = await fetch(`${global.API_URL}/policies-and-procedures`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });
    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    const data = await res.json();
    if (data.foundational) {
      // Merge API selected state with static list
      return allPolicies.map(policy => ({
        ...policy,
        isSelected: !!data.foundational[policy.identity]
      }));
    }
  } catch (error) {
    console.error("Failed to fetch policies for PDF:", error);
  }
  // fallback: all policies, none selected
  return allPolicies.map(policy => ({
    ...policy,
    isSelected: false
  }));
}

async function downloadPoliciesAndProceduresPDF(card) {
  showLoadingOverlay('Generating Policies and Procedures PDF...');
  let tempContainer = null;
  try {
    // Always fetch the latest policy selection state from the API
    const policiesList = await fetchPoliciesForPDF();

    tempContainer = document.createElement('div');
    tempContainer.style.width = '800px';
    tempContainer.style.padding = '40px';
    tempContainer.style.backgroundColor = 'white';
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';

    let tableHtml = '';
    if (policiesList && policiesList.length > 0) {
      tableHtml = `
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
          <thead>
            <tr>
              <th style="text-align:left; padding: 8px; border-bottom: 2px solid #00423D;">Selected</th>
              <th style="text-align:left; padding: 8px; border-bottom: 2px solid #00423D;">Policy Name</th>
              <th style="text-align:left; padding: 8px; border-bottom: 2px solid #00423D;">Description</th>
            </tr>
          </thead>
          <tbody>
            ${policiesList.map(policy => `
              <tr>
                <td style="padding: 8px; font-size: 18px;">${policy.isSelected ? '☑' : '☐'}</td>
                <td style="padding: 8px; color: #00423D; font-weight: bold; text-transform: uppercase;">${policy.name}</td>
                <td style="padding: 8px; font-size: 14px; color: #414141;">${policy.description}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    } else {
      tableHtml = `<div style="color: #E25C5C; margin-top: 20px;">No policies found. Please ensure your policies are set up and try again.</div>`;
    }

    tempContainer.innerHTML = `
      <div style="font-family: sans-serif; color: #414141;">
        <h2 style="font-size: 24px; color: #00423D; margin-bottom: 20px;">Policies and Procedures Manual</h2>
        <div style="font-size: 14px; line-height: 1.6; margin-bottom: 10px;">
          Below is a list of foundational policies and procedures that your organization should have in place if it is fundraising. Select the policies and procedures your organization needs to put into place or improve/revise.
        </div>
        ${tableHtml}
      </div>
    `;

    document.body.appendChild(tempContainer);
    await nextTick();
    const canvas = await html2canvas(tempContainer, {
      backgroundColor: '#ffffff',
      useCORS: true,
      scale: 2,
      logging: false,
      width: 800,
      height: tempContainer.offsetHeight,
      allowTaint: true,
      removeContainer: false
    });
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let imgWidth = pageWidth - 40;
    let imgHeight = (canvas.height * imgWidth) / canvas.width;
    if (imgHeight > pageHeight - 40) {
      imgHeight = pageHeight - 40;
      imgWidth = (canvas.width * imgHeight) / canvas.height;
    }
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 20, 20, imgWidth, imgHeight);
    pdf.save('Policies_and_Procedures_Manual.pdf');
  } catch (error) {
    console.error('Failed to generate policies and procedures PDF:', error);
  } finally {
    if (tempContainer && tempContainer.parentNode) {
      document.body.removeChild(tempContainer);
    }
    hideLoadingOverlay();
  }
}
</script>

<style scoped>
/* Inherit scrollbar styles from StepsNav component */
.right-content-grid-vs::-webkit-scrollbar {
    width: 8px;
}

.right-content-grid-vs::-webkit-scrollbar-track {
    background: #EBE8E1;
}

.right-content-grid-vs::-webkit-scrollbar-thumb {
    background: #00423D;
    border-radius: 6px;
}

/* Fade-in animation for loading overlay */
@keyframes fade-in {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.animate-fade-in {
    animation: fade-in 0.3s ease-in-out;
}

/* Highlight animation for card */
.highlight-animate {
    position: relative;
    z-index: 1;
}

.highlight-animate::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 2px solid #00423D;
    border-radius: 12px;
    animation: highlight-pulse 10s ease-in-out;
    pointer-events: none;
}

@keyframes highlight-pulse {
    0% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(0, 66, 61, 0.4);
    }
    10% {
        transform: scale(1.02);
        box-shadow: 0 0 20px 10px rgba(0, 66, 61, 0.2);
    }
    20% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(0, 66, 61, 0);
    }
    30% {
        transform: scale(1.02);
        box-shadow: 0 0 20px 10px rgba(0, 66, 61, 0.2);
    }
    40% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(0, 66, 61, 0);
    }
    50% {
        transform: scale(1.02);
        box-shadow: 0 0 20px 10px rgba(0, 66, 61, 0.2);
    }
    60% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(0, 66, 61, 0);
    }
    70% {
        transform: scale(1.02);
        box-shadow: 0 0 20px 10px rgba(0, 66, 61, 0.2);
    }
    80% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(0, 66, 61, 0);
    }
    90% {
        transform: scale(1.02);
        box-shadow: 0 0 20px 10px rgba(0, 66, 61, 0.2);
    }
    100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(0, 66, 61, 0);
    }
}
</style>